import React, { useState, useRef, useEffect } from 'react';
import NameWithEmailTooltip from './NameWithEmailTooltip';
import type { Employee } from '../../services/selfAppraisalService';

interface MultiSelectSearchFieldProps {
  options: Employee[];
  emailOptions?: Employee[];
  excludedEmails?: string[];
  selectedValues: Employee[];
  excludedValues?: Employee[];
  onSelectionChange: (values: Employee[]) => void;
  placeholder?: string;
  disabled?: boolean;
  maxSelections?: number;
  label?: string;
  required?: boolean;
  description?: string;
  allowExternalEmail?: boolean;
}

const MultiSelectSearchField: React.FC<MultiSelectSearchFieldProps> = ({
  options,
  emailOptions = [],
  excludedEmails = [],
  selectedValues,
  excludedValues = [],
  onSelectionChange,
  placeholder = "Search and select options...",
  disabled = false,
  maxSelections,
  label,
  required = false,
  description,
  allowExternalEmail = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Removed unused highlightedIndex and setHighlightedIndex
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isEmployeeExcluded = React.useCallback((option: Employee) => {
    const normalizedOptionEmail = option.email?.trim().toLowerCase();
    if (
      normalizedOptionEmail &&
      excludedEmails.some(
        (email) => email.trim().toLowerCase() === normalizedOptionEmail,
      )
    ) {
      return true;
    }

    return excludedValues.some((excluded) => {
      if (excluded.email && option.email) {
        return excluded.email.trim().toLowerCase() === option.email.trim().toLowerCase();
      }
      return excluded.id === option.id || excluded.emp_id === option.emp_id;
    });
  }, [excludedEmails, excludedValues]);

  const availableOptions = React.useMemo(
    () => options.filter((option) => !isEmployeeExcluded(option)),
    [isEmployeeExcluded, options],
  );

  // Filter options based on search term (include all options, don't exclude selected ones)
  // (removed duplicate declaration, see useMemo below)
  // Helper function to check if an employee is selected
  const isEmployeeSelected = React.useCallback((option: Employee) => {
    return selectedValues.some(selected => {
      // Primary comparison: email (most reliable unique identifier)
      if (selected.email && option.email) {
        return selected.email === option.email;
      }
      // Fallback comparisons if email is not available
      return selected.id === option.id || 
             selected.emp_id === option.emp_id;
    });
  }, [selectedValues]);

  // Always show selected options, even if they don't match the search term, in their natural order
  const dropdownOptions = React.useMemo(() => {
    if (!searchTerm) {
      // When no search term, show all options in their original order
      return availableOptions;
    }
    
    // When searching, show options that match the search term (name OR email) OR are selected
    const searchLower = searchTerm.toLowerCase();
    return availableOptions.filter(option => {
      const matchesName = option.full_name.toLowerCase().includes(searchLower);
      const matchesEmail = option.email?.toLowerCase().includes(searchLower);
      const isSelected = isEmployeeSelected(option);
      return matchesName || matchesEmail || isSelected;
    });
  }, [availableOptions, searchTerm, isEmployeeSelected]);
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const hasMatchingOption = normalizedSearchTerm.length > 0 &&
    availableOptions.some((option) =>
      option.full_name.toLowerCase().includes(normalizedSearchTerm) ||
      option.email?.toLowerCase().includes(normalizedSearchTerm),
    );

  const externalEmailOption: Employee = {
    id: 0,
    emp_id: 0,
    email: searchTerm.trim(),
    full_name: searchTerm.trim(),
    isExternal: true,
  };
  const normalizedSearchEmail = searchTerm.trim().toLowerCase();
  const isPetronasComMyEmail = normalizedSearchEmail.endsWith('@petronas.com.my');
  const emailAlreadyInOptions = [...options, ...emailOptions].some(
    (option) => option.email?.trim().toLowerCase() === normalizedSearchEmail,
  );
  const emailAlreadySelectedElsewhere = excludedValues.some(
    (excluded) => excluded.email?.trim().toLowerCase() === normalizedSearchEmail,
  );
  const emailBelongsToExcludedUser = excludedEmails.some(
    (email) => email.trim().toLowerCase() === normalizedSearchEmail,
  );
  const canAddExternalEmail =
    allowExternalEmail &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(searchTerm.trim()) &&
    !isPetronasComMyEmail &&
    !emailAlreadyInOptions &&
    !emailAlreadySelectedElsewhere &&
    !emailBelongsToExcludedUser &&
    !selectedValues.some(
      (selected) =>
        selected.isExternal &&
        selected.email?.trim().toLowerCase() === normalizedSearchEmail,
    );
   // Dropdown options: selected at top, then filtered unselected
  // Removed unused dropdownOptions

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
            // removed: setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  // (removed duplicate lower declaration)

  // Move useEffect below handleOptionSelect declaration
  const handleOptionSelect = React.useCallback((option: Employee) => {
     const isAlreadySelected = isEmployeeSelected(option);
     if (!isAlreadySelected && isEmployeeExcluded(option)) {
       return;
     }
     if (isAlreadySelected) {
       // If already selected, remove it - find and remove by email (primary) or ID
       const newSelectedValues = selectedValues.filter((selected) => {
         if (selected.email && option.email) {
           return selected.email !== option.email;
         }
         return !(selected.id === option.id || selected.emp_id === option.emp_id);
       });
       onSelectionChange(newSelectedValues);
     } else {
       // If not selected, add it (if under the limit)
       if (maxSelections && selectedValues.length >= maxSelections) {
         return; // Don't allow more selections than the limit
       }
       const newSelectedValues = [...selectedValues, option];
       onSelectionChange(newSelectedValues);
     }
     if (option.isExternal) {
       setIsOpen(false);
       setSearchTerm('');
       return;
     }
     // Keep dropdown open for multi-selection, and keep search term to continue searching
     inputRef.current?.focus();
  }, [selectedValues, onSelectionChange, maxSelections, isEmployeeExcluded, isEmployeeSelected]);

  const handleRemoveSelection = (employeeToRemove: Employee) => {
    const newSelectedValues = selectedValues.filter((selected) => {
      // Remove by email (primary) or ID
      if (selected.email && employeeToRemove.email) {
        return selected.email !== employeeToRemove.email;
      }
      return !(selected.id === employeeToRemove.id || selected.emp_id === employeeToRemove.emp_id);
    });
    onSelectionChange(newSelectedValues);
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
        // removed: setHighlightedIndex(-1);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Label */}
      {label && (
        <div className="mb-3">
          <span className="font-medium text-gray-700">{label}</span>
          {required && <span className="text-red-500 ml-1">*</span>}
          {description && (
            <span className="text-xs text-gray-500 italic ml-2">
              {description}
            </span>
          )}
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={
            maxSelections && selectedValues.length >= maxSelections
              ? `Maximum ${maxSelections} selections reached`
              : placeholder
          }
          disabled={disabled || Boolean(maxSelections && selectedValues.length >= maxSelections)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-700 placeholder-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200"
        />
        
        {/* Dropdown Icon */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'transform rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {/* Dropdown Options */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {canAddExternalEmail && dropdownOptions.length > 0 && (
            <div
              onClick={() => handleOptionSelect(externalEmailOption)}
              className="px-4 py-3 cursor-pointer text-purple-700 hover:bg-purple-50 border-b border-gray-100"
            >
              Add email: <span className="font-medium">{searchTerm.trim()}</span>
            </div>
          )}
          {allowExternalEmail && isPetronasComMyEmail && dropdownOptions.length > 0 && (
            <div className="px-4 py-3 text-sm text-red-600">
              Email addresses ending in @petronas.com.my cannot be added.
            </div>
          )}
          {dropdownOptions.length > 0 ? (
            <>
              {dropdownOptions.map((option, index) => {
                const isSelected = isEmployeeSelected(option);
                
                return (
                  <div
                    key={`${option.email || option.emp_id || option.id}`}
                    onClick={() => handleOptionSelect(option)}
                    className={`px-4 py-3 cursor-pointer transition-colors duration-150 flex items-center w-full ${
                      isSelected
                        ? 'bg-purple-50 text-purple-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    } ${index === 0 ? 'rounded-t-xl' : ''} ${index === dropdownOptions.length - 1 && (!normalizedSearchTerm || hasMatchingOption || canAddExternalEmail) ? 'rounded-b-xl' : ''}`}
                  >
                    <div className="flex items-center gap-3 w-full">
                      <div
                        className={`w-5 h-5 flex-shrink-0 flex items-center justify-center rounded border ${
                          isSelected ? 'border-purple-500 bg-purple-100' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {isSelected && (
                          <svg
                            className="w-4 h-4 text-purple-600"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="flex-1">{option.full_name}</span> <span> ({option.email})</span>
                    </div>
                  </div>
                );
              })}
              {normalizedSearchTerm &&
                !hasMatchingOption &&
                !canAddExternalEmail &&
                (!isPetronasComMyEmail || !allowExternalEmail) && (
                <div className="px-4 py-3 text-gray-500 italic">
                  {allowExternalEmail
                    ? 'No matching options found. Type a valid email address to add it.'
                    : 'No matching options found.'}
                </div>
              )}
            </>
          ) : (
            <div className="px-4 py-3 text-gray-500 italic">
              {!canAddExternalEmail && (searchTerm
                ? isPetronasComMyEmail && allowExternalEmail
                  ? 'Email addresses ending in @petronas.com.my cannot be added.'
                  : allowExternalEmail
                    ? 'No matching options found. Type a valid email address to add it.'
                    : 'No matching options found.'
                : 'No options available')}
              {canAddExternalEmail && (
                <div
                  onClick={() => handleOptionSelect(externalEmailOption)}
                  className="mt-2 not-italic cursor-pointer text-purple-700 hover:text-purple-900"
                >
                  Add email: <span className="font-medium">{searchTerm.trim()}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Selected Values Display - Moved to bottom */}
      {/* Selected Values Display - Moved to bottom */}
      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {selectedValues.map((employee) => {
            return (
              <span
                key={employee.email || employee.emp_id || employee.id}
                className="bg-purple-100 text-purple-700 px-3 py-1.5 rounded text-base border border-purple-200 flex items-center"
              >
                <NameWithEmailTooltip
                  as="span"
                  name={employee.full_name?.trim() || employee.email?.trim()}
                  email={employee.email}
                  className=""
                />
                {!disabled && (
                  <button
                    onClick={() => handleRemoveSelection(employee)}
                    className="ml-2 text-base text-purple-700 hover:text-purple-900"
                    type="button"
                  >
                    ×
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {/* Helper Text */}
      {maxSelections && (
        <div className="mt-2 text-xs text-gray-500">
          {selectedValues.length} of {maxSelections} selections made
        </div>
      )}
    </div>
  );
};

export default MultiSelectSearchField;
